import React, { useState, useEffect, useMemo } from 'react';
import theme from './ProjectSettings.module.scss';
import { Trans, useLingui } from '@lingui/react/macro';
import { useLingui as useLinguiNoMacro } from '@lingui/react';
import { ValidatingInput, ValidatingSelect, ValidationSummary, ValidationForm } from '@/components/forms/validation';
import { useAppDispatch, useAsyncState, useMergeState } from '@/utils/hooks';
import { getCurrencies } from '@/services/currencies';
import {
    getProject,
    getProjectHeaderImageUrl,
    createOrUpdateProject,
    UpdateProjectRequest,
    getProjectFeatures,
    saveProjectFeatures,
} from '@/services/projects';
import {
    TranslatableText,
    Project,
    UnitOfMeasurement,
    ProjectDecimalOption,
    ProjectFeature,
    ProjectFeatureConfiguration,
} from '@/types';
import { ProjectFeatureToggle } from './ProjectFeatureToggle';
import ImageUpload from '@/components/generic-controls/ImageUpload';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { SupportedCultures } from '@/utils/localization';
import { classnames, toNullableNumber } from '@/utils/html';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { validateSubdomain } from '@/utils/url';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Button, SlideToggle } from '@ygtechnologies/rubus-ui';
import {
    Address,
    AddressAutocomplete,
    BygrModule,
    COUNTRY_CODES,
    CurrencyDisplay,
    InputWrapper,
    LoadingIndicator,
    Toast,
    ValidatingTextArea,
} from '@ygtechnologies/bygr-frontend-component-library';
import { setActiveProjectId } from '@/state/global/creators';

interface ProjectSettingsGeneralProps {
    projectId?: number;

    onSave?(project: Project): void;
}

const ProjectSettingsGeneral: React.FC<ProjectSettingsGeneralProps> = ({ projectId, onSave }) => {
    const activeProfile = useSelector((state: IState) => state.global.activeProfile!);
    const user = useSelector((state: IState) => state.global.user);
    const dispatch = useAppDispatch();
    const { _ } = useLinguiNoMacro();
    const { t } = useLingui();

    const locale = useMemo(() => {
        switch (user?.locale) {
            case 'no-NB':
                return 'NO';
            case 'sv-SE':
                return 'SE';
            case 'de-DE':
                return 'DE';
            default:
                return 'NO';
        }
    }, [user]);

    const [isSaving, setIsSaving] = useState(false);

    const [currencies] = useAsyncState(() => getCurrencies(), [], []);
    const [errors, setErrors] = useState<TranslatableText[]>([]);
    const [headerImage, setHeaderImage] = useState<FileData | null>(null);
    const [data, mergeData, setData] = useMergeState<Omit<UpdateProjectRequest, 'image'> | null>(null);

    // Feature configs are a separate model from the project entity (their own endpoint, keyed by
    // project id), so they load/save independently. ProjectFeatureToggle reads and patches this
    // array; we just hold it and persist it on save. Only available for existing projects.
    const [projectFeatures, setProjectFeatures] = useAsyncState<ProjectFeatureConfiguration[] | null>(
        () => (projectId ? getProjectFeatures(projectId) : Promise.resolve(null)),
        [projectId],
        null
    );

    useEffect(() => {
        const loadData = async () => {
            if (projectId) return setData(await getProject(projectId));
            setData({
                name: '',
                description: '',
                currencyCode: null,
                sellerProfileId: activeProfile.id,
                defaultLanguageCode: '',
                autoAssignOrder: false,
                changeRequestWarningText: null,
                subdomain: '',
                address: null,
                countryCode: locale,
                defaultShowOfferSubtotalsToBuyers: false,
                showLandingPageBackgroundImage: false,
                emailContactInfo: null,
                buyerOptionOneTimeFee: 0,
                modules: [BygrModule.ChangeManagement],
                defaultUnitOfMeasurement: UnitOfMeasurement.Millimeter,
                showBackgroundImageOnMessages: false,
                showDefaultTextOnOptions: false,
                useLegacyOptionsView: true,
                useRooms: false,
                use3dConfigurator: false,
                decimalOption: ProjectDecimalOption.None,
                absolutePricing: true,
            });
        };

        loadData();
    }, [projectId, setData]);

    if (!data) return <LoadingIndicator />;

    const getHeaderImage = () => {
        if (headerImage) {
            return headerImage;
        }

        if (data && data.id) {
            return getProjectHeaderImageUrl(data.id);
        }

        return null;
    };

    const handleSubmit = async (e) => {
        setErrors([]);
        setIsSaving(true);

        const request: UpdateProjectRequest = {
            ...data,
            image: headerImage,
        };

        let result: Awaited<ReturnType<typeof createOrUpdateProject>>;
        try {
            result = await createOrUpdateProject(request);

            // Feature configs save separately from the project entity (see ProjectFeatureToggle).
            if (projectId && projectFeatures) {
                await saveProjectFeatures(projectId, projectFeatures);
            }
        } catch (e: any) {
            // duplicate subdomain
            if (e.status === 409) {
                const errorMessage = <Trans>Subdomain name taken</Trans>;
                Toast.error(errorMessage);
                setErrors([errorMessage]);
            } else {
                Toast.error(<Trans>Something went wrong with saving</Trans>);
            }
            return;
        } finally {
            setIsSaving(false);
        }

        // The save is committed at this point — report success before the refresh below, so a
        // failed refetch can't read as a failed save.
        Toast.success(<Trans>Saved</Trans>);
        await setActiveProjectId(dispatch, result.id, true);
        triggerHandler(onSave, result);
    };

    return (
        <div className={theme.section}>
            <ValidationForm className="form columns-1 labels-20" onSubmit={handleSubmit}>
                <label>
                    <Trans>Name</Trans>
                    <span className={theme.required}>*</span>
                    <ValidatingInput
                        type="text"
                        required
                        className="form-control"
                        placeholder={t`Project name`}
                        value={data.name}
                        onChange={(e) => mergeData({ name: e.target.value })}
                    />
                </label>

                {/* <div className={theme.row}>
                    <span>
                        <Trans>Description</Trans>
                    </span>
                    <ValidatingInput
                        type="text"
                        className="form-control"
                        placeholder={t`Project description`}
                        name="name"
                        value={project.description}
                        onChange={(e) => setProject({ ...project, description: e.target.value })}
                    />
                </div> */}
                <label>
                    <Trans>Default language</Trans>
                    <span className={theme.required}>*</span>
                    <ValidatingSelect
                        className="form-control"
                        required
                        fieldName={t`Language`}
                        value={data.defaultLanguageCode}
                        onChange={(e) => mergeData({ defaultLanguageCode: e.target.value })}>
                        <option value="">{t`Please select`}</option>
                        {SupportedCultures.map((culture) => (
                            <option key={culture.locale} value={culture.locale}>
                                {culture.localName}
                            </option>
                        ))}
                    </ValidatingSelect>
                </label>

                <label>
                    <Trans>Country</Trans>
                    <span className={theme.required}>*</span>
                    <ValidatingSelect
                        className="form-control"
                        required
                        fieldName={t`Country`}
                        value={data.countryCode}
                        onChange={(e) => mergeData({ countryCode: e.target.value as UpdateProjectRequest['countryCode'] })}>
                        <option value="">{t`Please select`}</option>
                        {Object.entries(COUNTRY_CODES).map(([countryCode, countryName]) => (
                            <option key={countryCode} value={countryCode}>
                                {_({ id: countryName })}
                            </option>
                        ))}
                    </ValidatingSelect>
                </label>

                <label>
                    <Trans>Address</Trans>
                    <AddressAutocomplete
                        address={data.address}
                        setAddress={(address: Address | null) => mergeData({ address })}
                        countryCode={data.countryCode}
                    />
                </label>

                <label>
                    <Trans>Currency</Trans>
                    <span className={theme.required}>*</span>
                    <ValidatingSelect
                        className="form-control"
                        required
                        fieldName={t`Currency`}
                        value={data.currencyCode == null ? '' : data.currencyCode}
                        onChange={(e) => mergeData({ currencyCode: e.target.value })}>
                        <option value="">{t`Please select`}</option>
                        {currencies.map((currency) => (
                            <option key={currency.id} value={currency.code}>
                                {currency.code} - {currency.name}
                            </option>
                        ))}
                    </ValidatingSelect>
                </label>
                <label>
                    <Trans>Number of decimals shown</Trans>
                    <span className={theme.required}>*</span>

                    <InfoIconWithTooltip
                        content={
                            <>
                                <Trans>Number of decimals always shown on prices etc.</Trans>
                                <br />
                                0:{' '}
                                <CurrencyDisplay
                                    currency={data.currencyCode || 'NOK'}
                                    value={100.25}
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                />
                                <br />
                                2:{' '}
                                <CurrencyDisplay
                                    currency={data.currencyCode || 'NOK'}
                                    value={100.25}
                                    minimumFractionDigits={2}
                                    maximumFractionDigits={2}
                                />
                            </>
                        }
                    />
                    <ValidatingSelect
                        className="form-control"
                        required
                        fieldName={t`Number of decimals shown`}
                        value={data.decimalOption}
                        onChange={(e) => mergeData({ decimalOption: e.target.value as ProjectDecimalOption })}>
                        <option key={0} value={ProjectDecimalOption.None}>
                            0
                        </option>
                        <option key={2} value={ProjectDecimalOption.Two}>
                            2
                        </option>
                    </ValidatingSelect>
                </label>

                <label>
                    <Trans>Pricing mode</Trans>
                    <span className={theme.required}>*</span>

                    <InfoIconWithTooltip
                        content={
                            <>
                                <Trans>
                                    <strong>Relative:</strong> the buyer sees each option's price minus the group's default
                                    option, so the default is free and the others show the difference. The real product price
                                    is kept for the seller. Example: 100, 200, 300 is shown as 0, 100, 200.
                                </Trans>
                                <br />
                                <br />
                                <Trans>
                                    <strong>Absolute:</strong> the price you set is exactly what the buyer sees, with no
                                    automatic subtraction.
                                </Trans>
                            </>
                        }
                    />
                    <ValidatingSelect
                        className="form-control"
                        required
                        fieldName={t`Pricing mode`}
                        value={data.absolutePricing ? 'absolute' : 'relative'}
                        onChange={(e) => mergeData({ absolutePricing: e.target.value === 'absolute' })}>
                        <option key={'absolute'} value={'absolute'}>
                            {t`Absolute`}
                        </option>
                        <option key={'relative'} value={'relative'}>
                            {t`Relative`}
                        </option>
                    </ValidatingSelect>
                </label>

                <label>
                    <Trans>Default unit of measurement</Trans>
                    <span className={theme.required}>*</span>
                    <ValidatingSelect
                        className="form-control"
                        required
                        fieldName={t`Unit of measurement`}
                        value={data.defaultUnitOfMeasurement}
                        onChange={(e) => mergeData({ defaultUnitOfMeasurement: e.target.value as UnitOfMeasurement })}>
                        <option key={'mm'} value={UnitOfMeasurement.Millimeter}>
                            {t`mm (millimeters)`}
                        </option>
                        <option key={'cm'} value={UnitOfMeasurement.Centimeter}>
                            {t`cm (centimeters)`}
                        </option>
                    </ValidatingSelect>
                </label>

                <label>
                    <Trans>Subdomain</Trans>
                    <InputWrapper
                        appendContent={window.APP_CONFIG.APP_DOMAIN ? `.${window.APP_CONFIG.APP_DOMAIN}` : undefined}>
                        <ValidatingInput
                            style={{ width: '150px' }}
                            type="text"
                            className="form-control"
                            placeholder={t`Subdomain`}
                            value={data.subdomain || ''}
                            onChange={(e) => mergeData({ subdomain: e.target.value })}
                            validations={[
                                {
                                    isValid: (value) => validateSubdomain(value),
                                    summaryMessage: () => (
                                        <Trans>Subdomain can only contain alphanumeric characters and dashes</Trans>
                                    ),
                                },
                            ]}
                        />
                    </InputWrapper>
                </label>

                <div className={classnames('form-label', theme.imageUploadLabel)}>
                    <Trans>Header image</Trans>
                    <div className="form-control-unstyled">
                        <div className={classnames(theme.imageUploadContainer)}>
                            <ImageUpload
                                onChange={(image) => setHeaderImage(image)}
                                aspectRatio={16 / 10}
                                currentImage={getHeaderImage()}
                            />
                        </div>
                    </div>
                </div>

                {/* <div className="form-label">
                    <label>
                        <Trans>Use project image as background for landing page</Trans>
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            onChange={(e) => mergeData({ showLandingPageBackgroundImage: e.target.checked })}
                            checked={data.showLandingPageBackgroundImage}
                        />
                    </div>
                </div> */}
                <div className="form-label">
                    <label>
                        <Trans>Use project image as background for messages page</Trans>
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            onChange={(e) => mergeData({ showBackgroundImageOnMessages: e.target.checked })}
                            checked={data.showBackgroundImageOnMessages}
                        />
                    </div>
                </div>

                {/* <div className="form-label">
                    <label>
                        <Trans>Auto assign orders</Trans>

                        <InfoIconWithTooltip
                            content={<Trans>The orders will be assigned to the person who linked them.</Trans>}
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            onChange={(e) => mergeData({ autoAssignOrder: e.target.checked })}
                            checked={data.autoAssignOrder}
                        />
                    </div>
                </div> */}

                <div className="form-label">
                    <label>
                        <Trans>Change request warning</Trans>

                        <InfoIconWithTooltip
                            content={
                                <Trans>Buyer have to accept that they may have to pay for creating a change request.</Trans>
                            }
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            className={theme.warningTextToggle}
                            onChange={() =>
                                mergeData({
                                    changeRequestWarningText: data.changeRequestWarningText === null ? '' : null,
                                })
                            }
                            checked={data.changeRequestWarningText !== null}
                        />
                        {data.changeRequestWarningText !== null && (
                            <ValidatingTextArea
                                className={classnames('form-control', theme.warningTextInput)}
                                value={data.changeRequestWarningText}
                                placeholder={t`Warning text to the buyer`}
                                autoResize
                                minRows={1}
                                maxRows={8}
                                onChange={(e) =>
                                    mergeData({
                                        changeRequestWarningText: e.target.value,
                                    })
                                }
                            />
                        )}
                    </div>
                </div>

                <div className="form-label">
                    <label>
                        <Trans>Show subtotals to buyers</Trans>

                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    If enabled, the "Show subtotals to buyers" slider will be enabled by default when sending
                                    change request offers
                                </Trans>
                            }
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            onChange={(e) => mergeData({ defaultShowOfferSubtotalsToBuyers: e.target.checked })}
                            checked={data.defaultShowOfferSubtotalsToBuyers}
                        />
                    </div>
                </div>

                <div className="form-label">
                    <label>
                        <Trans>Contact information in invitation emails</Trans>

                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    You can add a line with contact information so that the buyer can contact the right
                                    person if they have any questions with the invitation mail. This text will appear in the
                                    buyer invitation email only. Use a whole sentence.
                                </Trans>
                            }
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            className={theme.warningTextToggle}
                            onChange={() =>
                                mergeData({
                                    emailContactInfo: data.emailContactInfo === null ? '' : null,
                                })
                            }
                            checked={data.emailContactInfo !== null}
                        />
                        {data.emailContactInfo !== null && (
                            <ValidatingInput
                                className={classnames('form-control', theme.warningTextInput)}
                                value={data.emailContactInfo}
                                placeholder={t`e.g name, email or phone number`}
                                onChange={(e) =>
                                    mergeData({
                                        emailContactInfo: e.target.value,
                                    })
                                }
                            />
                        )}
                    </div>
                </div>

                <div className="form-label">
                    <label>
                        <Trans>One-time fee for selecting options</Trans>

                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    A fee that will be added to the buyers if they make at least one menu choice which is not
                                    the standard delivery of the home as defined in Rubus.
                                </Trans>
                            }
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <ValidatingInput
                            style={{ width: '150px' }}
                            type="number"
                            required
                            className="form-control"
                            placeholder={t`Amount`}
                            value={data.buyerOptionOneTimeFee ?? ''}
                            onChange={(e) => mergeData({ buyerOptionOneTimeFee: toNullableNumber(e.target.value) })}
                        />
                    </div>
                </div>

                <div className="form-label">
                    <label>
                        <Trans>Show (Default) on products for buyers</Trans>

                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    If enabled, (Default) will be shown on the product options in the buyer's view.
                                </Trans>
                            }
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            onChange={(e) => mergeData({ showDefaultTextOnOptions: e.target.checked })}
                            checked={data.showDefaultTextOnOptions}
                        />
                    </div>
                </div>

                <div className="form-label">
                    <label>
                        <Trans>Use legacy options view</Trans>
                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    When enabled, both buyers and sellers see the legacy options page instead of the new
                                    options configurator.
                                </Trans>
                            }
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            onChange={(e) => mergeData({ useLegacyOptionsView: e.target.checked })}
                            checked={data.useLegacyOptionsView}
                        />
                    </div>
                </div>

                <div className="form-label">
                    <label>
                        <Trans>Enable rooms</Trans>

                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    If enabled, room features are available in the project: managing rooms, assigning rooms
                                    to units and linking options to rooms.
                                </Trans>
                            }
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle onChange={(e) => mergeData({ useRooms: e.target.checked })} checked={data.useRooms} />
                    </div>
                </div>

                <div className="form-label">
                    <label>
                        <Trans>Enable 3D configurator</Trans>

                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    If enabled, the 3D configurator tabs are available under Options: registering 3D
                                    configurators and mapping options to them.
                                </Trans>
                            }
                        />
                    </label>
                    <div className="form-control-unstyled">
                        <SlideToggle
                            onChange={(e) => mergeData({ use3dConfigurator: e.target.checked })}
                            checked={data.use3dConfigurator}
                        />
                    </div>
                </div>

                {projectId && projectFeatures && (
                    <div className="form-label">
                        <label>
                            <Trans>Enable showroom</Trans>

                            <InfoIconWithTooltip
                                content={
                                    <Trans>
                                        When enabled, an anonymous showroom link is available for the units in this project,
                                        letting prospective buyers explore the options without signing in.
                                    </Trans>
                                }
                            />
                        </label>
                        <div className="form-control-unstyled">
                            <ProjectFeatureToggle
                                toggle={ProjectFeature.Showroom}
                                projectFeatures={projectFeatures}
                                onChange={setProjectFeatures}
                            />
                        </div>
                    </div>
                )}

                <ValidationSummary externalErrors={errors} />

                <div className="buttons right">
                    <Button className={theme.saveButton} type="submit" variant="primary" loading={isSaving}>
                        {projectId ? <Trans>Save</Trans> : <Trans>Create new project</Trans>}
                    </Button>
                </div>
            </ValidationForm>
        </div>
    );
};

export default ProjectSettingsGeneral;
